import React, { useState, useEffect } from 'react';
import {
  Layout, CheckSquare, Calendar, Settings, PanelLeftOpen,
  PanelLeftClose, X, Crown, Flag, Loader2, Users, Plus, Mail, Trash2,
  FileText, MessageCircle, Sparkles, Activity // <--- Added MessageCircle, Sparkles for Chat
} from 'lucide-react';
import { THEME } from '../theme';

const API_BASE_URL = (process.env.REACT_APP_API_URL || "").replace("http://", "https://");

// --- REPORT MODAL (Unchanged) ---
const ReportModal = ({ isOpen, onClose, darkMode, userEmail }) => {
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!message.trim()) return;

    setLoading(true);
    let finalEmail = userEmail;
    if (!finalEmail) {
      try {
        const stored = localStorage.getItem('userInfo') || localStorage.getItem('user');
        if (stored) {
          const parsed = JSON.parse(stored);
          finalEmail = parsed.email || parsed?.user?.email;
        }
      } catch (err) { }
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/report`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: finalEmail || 'anonymous@hicapy.com',
          message: message
        }),
      });

      if (response.ok) {
        setSuccess(true);
        setTimeout(() => {
          setSuccess(false);
          setMessage('');
          onClose();
        }, 2000);
      } else {
        alert('Failed to send report.');
      }
    } catch (error) {
      alert('Network error.');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center px-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className={`relative w-full max-w-md rounded-2xl p-6 shadow-2xl transition-all ${darkMode ? 'bg-slate-800 border border-white/10 text-white' : 'bg-white border border-slate-200 text-slate-900'}`}>
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium flex items-center gap-2"><Flag size={18} className="text-secondary" /> Report an Issue</h3>
          <button onClick={onClose} className="opacity-70 hover:opacity-100"><X size={20} /></button>
        </div>
        {success ? (
          <div className="py-8 text-center"><div className="w-12 h-12 bg-emerald-500/20 text-emerald-500 rounded-full flex items-center justify-center mx-auto mb-3"><CheckSquare size={24} /></div><p>Report received!</p></div>
        ) : (
          <form onSubmit={handleSubmit}>
            <textarea value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Describe the issue..." className={`w-full h-32 rounded-xl p-3 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-secondary/50 mb-4 font-light ${darkMode ? 'bg-black/20 border border-white/10' : 'bg-slate-50 border-slate-200'}`} required />
            <div className="flex gap-3 justify-end">
              <button type="button" onClick={onClose} className="px-4 py-2 rounded-xl text-sm font-medium hover:opacity-70">Cancel</button>
              <button type="submit" disabled={loading} className="px-4 py-2 rounded-xl text-sm font-medium bg-secondary text-white hover:bg-blue-600 disabled:opacity-50 flex items-center gap-2">{loading && <Loader2 size={16} className="animate-spin" />} Submit</button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

// --- SIDEBAR COMPONENT (Updated with Tasks/Inbox) ---
const Sidebar = ({ activeTab, setActiveTab, isOpen, setIsOpen, darkMode, isCollapsed, toggleCollapse, userEmail, onUpgradeClick }) => {
  const [isReportOpen, setIsReportOpen] = useState(false);

  // --- TEAM STATE ---
  const [team, setTeam] = useState([]);
  const [inviteEmail, setInviteEmail] = useState('');
  const [isInviting, setIsInviting] = useState(false);

  // --- MENU ITEMS UPDATED ---
  const menuItems = [
    { id: 'chat', icon: Sparkles, label: 'Assistant', highlight: true },  // <--- MAIN AI CHAT
    { id: 'dashboard', icon: Layout, label: 'Dashboard' },
    { id: 'notes', icon: FileText, label: 'Notes' },
    { id: 'tasks', icon: CheckSquare, label: 'Inbox' },
    { id: 'integrations', icon: Calendar, label: 'Integrations' },
    { id: 'settings', icon: Settings, label: 'Settings' },
  ];

  // --- FETCH TEAM ---
  useEffect(() => {
    const fetchTeam = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;
        const res = await fetch(`${API_BASE_URL}/api/team`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          if (data.success) setTeam(data.team || []);
        }
      } catch (e) { console.error("Team fetch error", e); }
    };
    fetchTeam();
  }, []);

  // --- TEAM HANDLERS ---
  const handleInvite = async (e) => {
    e.preventDefault();
    if (!inviteEmail.trim()) return;
    setIsInviting(true);

    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE_URL}/api/team/invite`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: inviteEmail })
      });
      const data = await res.json();

      if (res.ok && data.success) {
        setTeam([...team, data.member]);
        setInviteEmail('');
      } else {
        alert(data.error || "Failed to find user.");
      }
    } catch (err) { alert("Network error"); }
    finally { setIsInviting(false); }
  };

  const handleRemoveMember = async (id) => {
    if (!window.confirm("Remove this member?")) return;
    try {
      const token = localStorage.getItem('token');
      await fetch(`${API_BASE_URL}/api/team/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setTeam(team.filter(m => m._id !== id));
    } catch (e) { alert("Failed to remove"); }
  };

  return (
    <>
      <ReportModal isOpen={isReportOpen} onClose={() => setIsReportOpen(false)} darkMode={darkMode} userEmail={userEmail} />

      {isOpen && <div className="fixed inset-0 bg-black/50 z-40 md:hidden" onClick={() => setIsOpen(false)} />}

      <div className={`
        fixed inset-y-0 left-0 z-50 transform transition-all duration-300 ease-in-out
        md:relative md:translate-x-0 border-r flex flex-col overflow-x-hidden
        ${isOpen ? 'translate-x-0' : '-translate-x-full'}
        ${isCollapsed ? 'w-20' : 'w-64'}
        ${darkMode ? 'bg-primary border-white/5' : 'bg-white border-slate-200'}
      `}>

        {/* HEADER */}
        <div className={`flex items-center ${isCollapsed ? 'justify-center flex-col gap-4' : 'justify-between'} px-4 py-6 border-b ${darkMode ? 'border-white/5' : 'border-slate-200'} relative h-24 shrink-0`}>
          <div className="flex items-center space-x-3 overflow-hidden">
            <div className="w-8 h-8 flex-shrink-0"><img src="/hicapy.png" alt="Logo" className="w-full h-full object-contain" /></div>
            {!isCollapsed && <span className={`font-light text-xl whitespace-nowrap transition-opacity duration-200 ${darkMode ? 'text-white' : 'text-slate-900'}`}>HiCapy</span>}
          </div>
          <button onClick={toggleCollapse} className={`hidden md:flex p-1.5 rounded-lg transition-colors absolute ${isCollapsed ? 'top-[70px]' : 'right-4'} ${darkMode ? 'text-slate-400 hover:text-white hover:bg-white/5' : 'text-slate-400 hover:text-slate-900 hover:bg-slate-100'}`}>
            {isCollapsed ? <PanelLeftOpen className="w-5 h-5" /> : <PanelLeftClose className="w-5 h-5" />}
          </button>
          <button onClick={() => setIsOpen(false)} className={`md:hidden ${darkMode ? 'text-white' : 'text-slate-900'}`}><X className="w-6 h-6" /></button>
        </div>

        {/* NAVIGATION */}
        <nav className="flex-1 p-4 space-y-2 overflow-y-auto custom-scrollbar">
          {menuItems.map((item) => (
            <button
              key={item.id}
              onClick={() => { setActiveTab(item.id); setIsOpen(false); }}
              className={`
                w-full flex items-center ${isCollapsed ? 'justify-center px-0' : 'space-x-3 px-4'} py-3 rounded-lg transition-all duration-200 group relative font-light
                ${item.highlight && activeTab !== item.id
                  ? (darkMode
                    ? 'text-secondary border border-secondary/30 hover:bg-secondary/10'
                    : 'text-secondary border border-secondary/30 hover:bg-secondary/5')
                  : activeTab === item.id
                    ? 'bg-secondary text-white'
                    : (darkMode ? 'text-slate-400 hover:bg-white/5 hover:text-white' : 'text-slate-500 hover:bg-slate-100 hover:text-slate-900')}
              `}
              title={isCollapsed ? item.label : ''}
            >
              <item.icon className="w-5 h-5 flex-shrink-0" />
              {!isCollapsed && <span className="whitespace-nowrap">{item.label}</span>}
              {!isCollapsed && item.highlight && activeTab !== item.id && (
                <span className="ml-auto text-[10px] px-1.5 py-0.5 rounded-full bg-secondary/10 text-secondary font-medium">AI</span>
              )}
              {isCollapsed && <div className="absolute left-full ml-2 px-2 py-1 rounded bg-slate-800 text-white text-xs font-medium opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity z-50 whitespace-nowrap">{item.label}</div>}
            </button>
          ))}
        </nav>

        {/* FOOTER (Report & Upgrade) */}
        <div className={`p-4 border-t ${darkMode ? 'border-white/5' : 'border-slate-200'} space-y-4 shrink-0`}>
          <button onClick={() => setIsReportOpen(true)} className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg transition-colors text-xs font-light ${isCollapsed ? 'justify-center' : ''} ${darkMode ? 'text-slate-400 hover:text-white hover:bg-white/5' : 'text-slate-500 hover:text-slate-900 hover:bg-slate-100'}`} title="Report Issue">
            <Flag size={16} />{!isCollapsed && <span>Report Issue</span>}
          </button>
          {!isCollapsed && (
            <div className={`relative overflow-hidden rounded-xl p-5 ${darkMode ? 'bg-slate-800/50 border border-white/5' : 'bg-slate-50 border border-slate-200'}`}>
              <div className="flex items-start gap-3 relative z-10 mb-3"><div className="p-2 rounded-lg bg-secondary text-white"><Crown size={18} /></div><div><h4 className={`font-medium text-sm leading-tight ${darkMode ? 'text-white' : 'text-slate-900'}`}>Pro Plan</h4><p className={`text-[10px] mt-0.5 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Unlock AI features</p></div></div>
              <button onClick={onUpgradeClick} className={`w-full py-2 rounded-lg text-xs font-medium transition-all relative z-10 ${darkMode ? 'bg-white text-slate-900 hover:bg-slate-100' : 'bg-slate-900 text-white hover:bg-slate-800'}`}>Upgrade Now</button>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default Sidebar;